import { useCallback, useEffect, useState } from 'react';

/**
 * Local camera and microphone for the device test of the lobby and the guest's waiting room
 * (`navigator.mediaDevices` directly — no LiveKit needed): the preview stream, the device lists for the
 * pills („Kamera · <Label>", „Mikrofon · <Label>") and the chosen device ids that the call reuses.
 */

export interface DeviceOption {
  deviceId: string;
  label: string;
}

/** `denied`: the browser or the person refused access; `unavailable`: no device or no media API. */
export type MediaProblem = 'denied' | 'unavailable' | null;

export interface LocalMedia {
  stream: MediaStream | null;
  problem: MediaProblem;
  cameras: DeviceOption[];
  microphones: DeviceOption[];
  cameraId: string | null;
  microphoneId: string | null;
  cameraLabel: string | null;
  microphoneLabel: string | null;
  setCameraId: (id: string) => void;
  setMicrophoneId: (id: string) => void;
}

function deviceConstraint(id: string | null): MediaTrackConstraints | boolean {
  return id ? { deviceId: { exact: id } } : true;
}

function labelOf(track: MediaStreamTrack | undefined, devices: DeviceOption[]): string | null {
  if (!track) return null;
  const settingsId = track.getSettings().deviceId;
  const match = settingsId ? devices.find((device) => device.deviceId === settingsId) : undefined;
  return match?.label || track.label || devices[0]?.label || null;
}

/** Acquires camera + microphone while `enabled`; stops every track when disabled or unmounted. */
export function useLocalMedia(enabled: boolean): LocalMedia {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [problem, setProblem] = useState<MediaProblem>(null);
  const [cameras, setCameras] = useState<DeviceOption[]>([]);
  const [microphones, setMicrophones] = useState<DeviceOption[]>([]);
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [microphoneId, setMicrophoneId] = useState<string | null>(null);

  const refreshDevices = useCallback(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) return;
    const devices = await navigator.mediaDevices.enumerateDevices();
    const list = (kind: MediaDeviceKind, fallback: string) =>
      devices
        .filter((device) => device.kind === kind && device.deviceId !== '')
        .map((device, index) => ({
          deviceId: device.deviceId,
          label: device.label || `${fallback} ${index + 1}`,
        }));
    setCameras(list('videoinput', 'Kamera'));
    setMicrophones(list('audioinput', 'Mikrofon'));
  }, []);

  useEffect(() => {
    if (!enabled) {
      setStream(null);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setProblem('unavailable');
      return;
    }
    let cancelled = false;
    let acquired: MediaStream | null = null;
    navigator.mediaDevices
      .getUserMedia({ video: deviceConstraint(cameraId), audio: deviceConstraint(microphoneId) })
      .then(async (media) => {
        if (cancelled) {
          for (const track of media.getTracks()) track.stop();
          return;
        }
        acquired = media;
        setStream(media);
        setProblem(null);
        await refreshDevices();
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const name = error instanceof DOMException ? error.name : '';
        setProblem(name === 'NotAllowedError' || name === 'SecurityError' ? 'denied' : 'unavailable');
        setStream(null);
      });
    return () => {
      cancelled = true;
      if (acquired) for (const track of acquired.getTracks()) track.stop();
    };
  }, [enabled, cameraId, microphoneId, refreshDevices]);

  useEffect(() => {
    const devices = navigator.mediaDevices;
    if (!devices?.addEventListener) return;
    const onChange = () => void refreshDevices();
    devices.addEventListener('devicechange', onChange);
    return () => devices.removeEventListener('devicechange', onChange);
  }, [refreshDevices]);

  return {
    stream,
    problem,
    cameras,
    microphones,
    cameraId: cameraId ?? stream?.getVideoTracks()[0]?.getSettings().deviceId ?? null,
    microphoneId: microphoneId ?? stream?.getAudioTracks()[0]?.getSettings().deviceId ?? null,
    cameraLabel: labelOf(stream?.getVideoTracks()[0], cameras),
    microphoneLabel: labelOf(stream?.getAudioTracks()[0], microphones),
    setCameraId,
    setMicrophoneId,
  };
}

/** Text of the preview when there is no camera picture (no frame exists for these states). */
export function mediaProblemText(problem: MediaProblem): string {
  return problem === 'denied'
    ? 'Kein Zugriff auf Kamera und Mikrofon. Bitte erlauben Sie den Zugriff in Ihrem Browser.'
    : 'Keine Kamera oder kein Mikrofon gefunden.';
}
